import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Copy, Plus, Users } from "lucide-react";
import { AdminLayout } from "../AdminPages";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
const toSlug = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 31);
const copy = (text, what) => navigator.clipboard?.writeText(text).then(() => toast.success(`${what} copied`)).catch(() => toast.error("Couldn't copy"));

const Row = ({ r, onToggle, onLinkEmail }) => {
  const link = `${window.location.origin}/with/${r.slug}`;
  const stats = `${window.location.origin}/with-stats/${r.stats_token}`;
  return (
    <tr className={`border-t border-slate-100 ${r.active ? "" : "opacity-50"}`} data-testid={`influencer-row-${r.slug}`}>
      <td className="py-3 pr-3"><div className="flex items-center gap-2">{r.photo_url ? <img src={`${API}${r.photo_url}`} alt="" className="w-7 h-7 rounded-full object-cover" data-testid={`influencer-photo-${r.slug}`} /> : null}<div><p className="font-medium text-slate-900">{r.name}</p><p className="text-xs text-slate-500">/with/{r.slug}</p></div></div></td>
      <td className="py-3 px-3 text-center tabular-nums" data-testid={`influencer-visitors-${r.slug}`}>{r.visitors}</td>
      <td className="py-3 px-3 text-center tabular-nums" data-testid={`influencer-lights-${r.slug}`}>{r.lights}</td>
      <td className="py-3 px-3 text-center tabular-nums" data-testid={`influencer-signups-${r.slug}`}>{r.signups}</td>
      <td className="py-3 pl-3">
        <div className="flex flex-wrap gap-2 justify-end">
          <button type="button" onClick={() => copy(link, "Link")} className="inline-flex items-center gap-1 text-xs rounded-md border border-slate-300 px-2 py-1" data-testid={`influencer-copy-link-${r.slug}`}><Copy className="w-3 h-3" /> Link</button>
          <button type="button" onClick={() => copy(stats, "Stats link")} className="inline-flex items-center gap-1 text-xs rounded-md border border-slate-300 px-2 py-1" data-testid={`influencer-copy-stats-${r.slug}`}><Copy className="w-3 h-3" /> Their stats</button>
          <button type="button" onClick={() => onToggle(r)} className="text-xs rounded-md px-2 py-1 text-slate-500 hover:text-slate-800" data-testid={`influencer-toggle-${r.slug}`}>{r.active ? "Pause" : "Resume"}</button>
          <button type="button" onClick={() => onLinkEmail(r)} title="The email they sign in with, so 'My partner area' appears in their account" className="text-xs rounded-md px-2 py-1 text-slate-500 hover:text-slate-800" data-testid={`influencer-link-email-${r.slug}`}>{r.email ? `Account: ${r.email}` : "Link account"}</button>
        </div>
      </td>
    </tr>
  );
};

const Leaderboard = ({ items }) => {
  const live = items.filter((r) => r.active);
  const lights = live.reduce((n, r) => n + r.lights, 0);
  const people = live.reduce((n, r) => n + r.visitors, 0);
  if (!live.length) return null;
  return (
    <div className="mb-8 rounded-2xl p-6 text-amber-50" style={{ background: "linear-gradient(135deg, #0f1a14, #1d170c)" }} data-testid="influencer-leaderboard">
      <p className="text-[0.65rem] uppercase tracking-[0.3em] text-amber-300/80">Lights brought into the world</p>
      <p className="mt-2 text-2xl font-light text-amber-50" style={{ fontFamily: '"Cormorant Garamond", serif', color: "#FFF8E7" }} data-testid="leaderboard-total">
        Together they've brought {people.toLocaleString()} {people === 1 ? "person" : "people"} home and lit {lights.toLocaleString()} {lights === 1 ? "light" : "lights"}.
      </p>
      <ol className="mt-5 space-y-2">
        {live.map((r, i) => (
          <li key={r.slug} className="flex items-center gap-3 rounded-xl px-3 py-2" style={{ background: i === 0 ? "rgba(252,211,77,0.08)" : "rgba(255,255,255,0.03)" }} data-testid={`leaderboard-row-${r.slug}`}>
            <span className="w-6 text-center text-sm tabular-nums text-amber-300/80">{i + 1}</span>
            {r.photo_url ? <img src={`${API}${r.photo_url}`} alt="" className="w-8 h-8 rounded-full object-cover" /> : <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm bg-amber-200/10">{r.name[0]}</span>}
            <span className="flex-1 min-w-0 truncate" style={{ color: "#FFF8E7" }}>{r.name}</span>
            <span className="text-sm tabular-nums text-right" data-testid={`leaderboard-visitors-${r.slug}`}>{r.visitors.toLocaleString()} <span className="hidden sm:inline text-amber-100/50 text-xs">visitors</span></span>
            <span className="text-sm tabular-nums w-12 sm:w-20 text-right">{r.lights}<span className="text-amber-100/50 text-xs"> <span className="hidden sm:inline">lights</span><span className="sm:hidden">✦</span></span></span>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default function AdminInfluencers() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/api/admin/influencers`, auth()).then((r) => setItems(r.data.items)).catch(() => toast.error("Couldn't load influencers"));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/influencers`, { name, slug: slug || toSlug(name) }, auth());
      setName(""); setSlug(""); toast.success("Link created"); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail === "slug_taken" ? "That link name is already used" : "Use 2–31 letters, numbers or dashes");
    } finally { setBusy(false); }
  };
  const toggle = (r) => axios.patch(`${API}/api/admin/influencers/${r.slug}`, { active: !r.active }, auth()).then(load);
  const linkEmail = (r) => {
    const email = window.prompt(`Email ${r.name} signs in with (leave empty to unlink):`, r.email || "");
    if (email === null) return;
    axios.put(`${API}/api/admin/influencers/${r.slug}/email`, { email: email.trim() || null }, auth())
      .then(() => { toast.success(email.trim() ? "Linked. 'My partner area' now shows in their account." : "Unlinked"); load(); })
      .catch(() => toast.error("That doesn't look like a valid email"));
  };

  return (
    <AdminLayout title="Influencer Links" subtitle="One link per influencer. Visitors, lights lit and sign-ups are counted for 30 days after someone arrives.">
      <button type="button" onClick={() => nav("/admin")} className="mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700" data-testid="admin-influencers-back"><ArrowLeft className="w-4 h-4" /> Back to admin</button>
      <form onSubmit={add} className="mb-8 bg-white border border-slate-200 rounded-2xl p-5 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2" data-testid="admin-influencer-form">
        <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. Sarah Jones)" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="admin-influencer-name" />
        <input value={slug} onChange={(e) => setSlug(toSlug(e.target.value))} placeholder={`Link name: ${toSlug(name) || "sarah"}`} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="admin-influencer-slug" />
        <button type="submit" disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm" data-testid="admin-influencer-create"><Plus className="w-4 h-4" /> Create link</button>
      </form>
      <Leaderboard items={items} />
      <div className="bg-white border border-slate-200 rounded-2xl p-5 overflow-x-auto">
        {items.length === 0 ? <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="admin-influencers-empty"><Users className="w-4 h-4" /> No influencer links yet.</p> : (
          <table className="w-full text-sm" data-testid="admin-influencers-table">
            <thead><tr className="text-xs uppercase tracking-wide text-slate-500"><th className="text-left pb-2">Influencer</th><th className="pb-2 px-3">Visitors</th><th className="pb-2 px-3">Lights</th><th className="pb-2 px-3">Sign-ups</th><th /></tr></thead>
            <tbody>{items.map((r) => <Row key={r.slug} r={r} onToggle={toggle} onLinkEmail={linkEmail} />)}</tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
